import { TypedDocumentNode } from '@apollo/client';
import { useLazyQuery, useMutation, useReactiveVar } from '@apollo/client/react';
import { Mutation, MutationBlockNfcCardArgs, Query } from 'graphql/generated/graphqlTypes';
import { BLOCK_NFC_CARD } from 'graphql/mutations';
import { GET_CARDS } from 'graphql/queries';
import { useEffect } from 'react';
import { setCardsAction, updateCardsAction } from 'state/actions/cards';
import { cardsVar } from 'state/vars';
import { ignoreHandledMutationError } from 'utils/Mutation/ignoreHandledMutationError';
import { BaseHookProps } from '../shared/types';

// Typing the documents (instead of passing generics to the Apollo hooks) avoids the deprecated hook signatures.
const GET_CARDS_TYPED: TypedDocumentNode<Pick<Query, 'getNfcCards'>> = GET_CARDS;
const BLOCK_NFC_CARD_TYPED: TypedDocumentNode<
  Pick<Mutation, 'blockNfcCard'>,
  MutationBlockNfcCardArgs
> = BLOCK_NFC_CARD;

const useCards = ({ handleFetchError }: BaseHookProps) => {
  const cards = useReactiveVar(cardsVar);

  const [getCards, { loading }] = useLazyQuery(GET_CARDS_TYPED, { fetchPolicy: 'network-only' });

  useEffect(() => {
    const fetchCards = async () => {
      try {
        const { data } = await getCards();
        if (data?.getNfcCards) {
          setCardsAction(data.getNfcCards);
        }
      } catch {
        handleFetchError('Error while fetching cards');
      }
    };
    void fetchCards();
  }, []);

  const [updateBlockNfcCardMutation, { loading: updateBlockNfcCardMutationLoading }] = useMutation(
    BLOCK_NFC_CARD_TYPED,
    {
      onCompleted: (data) => {
        updateCardsAction(data.blockNfcCard);
      },
      onError: () => {
        handleFetchError('Error while blocking card');
      }
    }
  );

  const updateBlockNfcCard = async (reason: string, uuid: string) => {
    // Failure is already handled in the mutation's onError, so the rejection is ignored here.
    await ignoreHandledMutationError(
      updateBlockNfcCardMutation({
        variables: {
          reason,
          uuid
        },
        // Here Apollo doesn't update cache automatically, we have to do it manually
        update(cache, { data: updatedCard }) {
          const blockedCard = updatedCard?.blockNfcCard;
          if (!blockedCard) return;
          cache.updateQuery(
            { query: GET_CARDS_TYPED },
            (existing) =>
              existing && {
                getNfcCards: existing.getNfcCards.map((card) => (card.uuid === blockedCard.uuid ? blockedCard : card))
              }
          );
        }
      })
    );
  };

  return {
    cards,
    loading,
    updateBlockNfcCard,
    updateBlockNfcCardMutationLoading
  };
};

export default useCards;
